import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { useAppController } from 'customHooks/useAppController'
import { BomItemActivityController } from 'features/BillOfMaterials/components/BomItemActivities/BomItemActivityController'
import { fetchProject } from 'features/BillOfMaterials/store/asyncActions/fetchProject'
import { newProjectActions } from 'features/BillOfMaterials/store/projectReducer'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { BomItemType, PartTypeRow } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { ShowException } from 'store/Application/appActions'
import { materialActions } from 'store/MaterialSelector/MaterialsReducer'
import { SettingsAsyncActions } from 'store/Settings/SettingsAsyncActions'
import {
  RootAppState,
  useAppDispatch,
  useAppSelector,
} from 'store/configureStore'
import { ArticleSelectorUI } from '../../../model/ArticleSelectorUI'
import {
  KeywordDto,
  MaterialSummaryDto,
  WorkingStepType,
} from '../../../services/APIs/InternalAPI/internal-api.contracts'
import { MaterialActions } from '../../../store/MaterialSelector/MaterialStore'
import { DialogCloseSaveButtons } from '../DialogActionButtons/DialogCloseSaveButtons'
import { LoadingContainer } from '../LoadingContainer'
import './ArticleSelectorDialog.scss'
import AvailableArticles from './AvailableArticles'
import { MaterialSelectorModalHeader } from './MaterialSelectorModalHeader'
import { TokenSelector } from './TokenSelector'

type OwnProps = {
  showSaveButton: boolean
  modalId: string
  isPurchasingPrice: boolean
  open: boolean
  onClose: () => void
}

type StateProps = {
  articleSelectorUI: ArticleSelectorUI
  activeRows: PartTypeRow[]
  isLoading: boolean
  projectId?: string
  organizationId?: string
  openedMaterialSelectorPointer: BomItemPointer
  workingStepType?: WorkingStepType
}

type DispatchProps = {
  onChangeTokens: (
    selectedTokens: KeywordDto[],
    workingStepType: WorkingStepType,
  ) => void
  onMaterialSelected: (selectedMaterial: MaterialSummaryDto) => void
  saveRows: () => void
  addPurchasingData: (material) => void
}

export type ArticleSelectorDialogProps = StateProps & DispatchProps & OwnProps

const mapStateToProps = (state: RootAppState): StateProps => ({
  articleSelectorUI: state.materials.articleSelectorUI,
  activeRows: state.materials.activeRows,
  isLoading: state.materials.isLoading,
  projectId: state.project?.activeProject?.id,
  openedMaterialSelectorPointer: state.materials.materialHeaderPointer,
  organizationId: state.user.organizationContext?.id,
  workingStepType: state.materials.workingStepType,
})

const mapDispatchToProps = (dispatch): DispatchProps => ({
  onChangeTokens: (
    selectedTokens: KeywordDto[],
    workingStepType: WorkingStepType,
  ) => dispatch(MaterialActions.changeTokens(selectedTokens, workingStepType)),
  onMaterialSelected: (material: MaterialSummaryDto) =>
    dispatch(MaterialActions.selectMaterial(material)),
  saveRows: () => {
    {
      dispatch(MaterialActions.saveSelectedTokensAndMaterials())
    }
  },
  addPurchasingData: (material) =>
    dispatch(SettingsAsyncActions.addPurchasingData(material)),
})

const ArticleSelectorDialog = (props: ArticleSelectorDialogProps) => {
  const {
    onMaterialSelected,
    isPurchasingPrice,
    addPurchasingData,
    saveRows,
    workingStepType,
    articleSelectorUI,
    activeRows,
    showSaveButton,
    modalId,
    open,
    onClose,
    isLoading,
    onChangeTokens,
  } = props
  const { t } = useTranslation('project')
  const projectId = useAppSelector(projectSelectors.projectIdSelector)

  const handleMaterialSelected = useCallback(
    (material: MaterialSummaryDto, shouldSave = false) => {
      onMaterialSelected(material)

      if (isPurchasingPrice) {
        addPurchasingData(material)
      }

      if (shouldSave && saveRows) {
        saveRows()
      }
    },
    [onMaterialSelected, isPurchasingPrice, addPurchasingData, saveRows],
  )

  const { controller, loading } = useAppController(
    () => new BomItemActivityController(),
  )

  const dispatch = useAppDispatch()

  const handleClose = () => {
    dispatch(materialActions.closeWithoutSave())
    onClose()
  }

  const handleSaveSelectedArticle = async () => {
    try {
      const isRawMaterial = activeRows[0]?.activities.find(
        (x) => x.primaryWorkingStep === workingStepType,
      )?.usesRawMaterial

      await controller.SetBomItemActivityArticle(
        activeRows.map((row) => {
          return {
            bomItemPointer: {
              id: row?.id,
              type: row.type,
            },
            activityId: row.activities.find(
              (x) => x.primaryWorkingStep === workingStepType,
            )?.id,
            workingStepType: workingStepType,
          }
        }),
        articleSelectorUI.selectedMaterial?.id,
        articleSelectorUI.selectedMaterial?.description,
        articleSelectorUI.selectedTokens,
        isRawMaterial,
      )

      handleClose()

      dispatch(
        newProjectActions.setBomItemInSearch({
          id: activeRows[0]?.id,
          type: BomItemType.partType,
        }),
      )
    } catch (err) {
      ShowException('Article Selector', err)
    } finally {
      dispatch(fetchProject({ projectId: projectId }))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableRestoreFocus
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle>
        <MaterialSelectorModalHeader
          {...activeRows[0]}
          title={t('article-selector--title', 'article selector')}
        />
      </DialogTitle>
      <DialogContent className="material-selector--content">
        <LoadingContainer loading={isLoading} variant="spinner" noMessage>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              height: '100%',
              alignItems: 'stretch',
            }}
          >
            <div style={{ flexGrow: 1, flexBasis: '40%' }}>
              <TokenSelector
                {...articleSelectorUI}
                width={'100%'}
                onTokensChanges={(tokens) => {
                  onChangeTokens(tokens, workingStepType)
                }}
              />
            </div>
            <div style={{ flex: 1, flexBasis: '60%' }}>
              <AvailableArticles
                selectedMaterial={articleSelectorUI?.selectedMaterial}
                materials={articleSelectorUI?.materials}
                width={'100%'}
                onMaterialSelected={handleMaterialSelected}
              />
            </div>
          </div>
        </LoadingContainer>
      </DialogContent>
      <DialogActions
        sx={{ flexDirection: 'column', alignItems: 'flex-end', gap: '.5em' }}
      >
        <div style={{ display: 'flex', gap: '.5em' }}>
          <DialogCloseSaveButtons
            onCloseButtonClicked={onClose}
            onSaveButtonClicked={() => {
              handleSaveSelectedArticle()
            }}
            saveButtonDisabled={!showSaveButton}
            saving={loading['bomitem-keywords']}
          />
        </div>
        {activeRows?.[0] && activeRows[0]['materialSummary']?.id && (
          <Typography variant="caption">
            {`${activeRows[0]['materialSummary'].id}: ${activeRows[0]['materialSummary'].description}`}
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ArticleSelectorDialog)
